// Quelle: /opt/webgis/frontend/src/components/ui/Toaster.tsx (Stand 2026-09-29)
// Geändert: Texte deutsch; liest useStore aus SPEC 03 §6.1; Toast mit optionalem Link
// (`link: {text, zu}` als <Link> unter der Meldung, z. B. „Protokoll“ nach einem Export);
// Behälter aria-live="polite"; ≤ 859 px steht er über der Reiterleiste.
import { useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { useStore, type Toast } from "../../store/useStore";
import { Icon } from "./Icon";
import { IconBtn, portalRoot } from "./primitives";

const TONE: Record<string, string> = { success: "good", error: "error", info: "teal", warn: "warn" };
const ICONS: Record<string, string> = { success: "check-circle", error: "warning-circle", info: "info", warn: "warning" };

function ToastCard({ t, onClose }: { t: Toast; onClose: () => void }) {
  const [copied, setCopied] = useState(false);
  const tone = TONE[t.type] ?? "teal";
  const ic = ICONS[t.type] ?? "info";
  const col = `var(--${tone === "teal" ? "accent-text" : tone})`;
  const copy = () => {
    if (!t.copy) return;
    void navigator.clipboard?.writeText(t.copy).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    });
  };
  return (
    <div
      className="glass-strong anim-slide-r toast-karte"
      role={t.type === "error" ? "alert" : undefined}
      style={{ borderRadius: 14, padding: "13px 15px", boxShadow: "var(--shadow-lg)", display: "flex", gap: 11, alignItems: "flex-start", borderLeft: `3px solid ${col}` }}
    >
      <Icon name={ic} size={19} weight="fill" style={{ color: col, marginTop: 1 }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <p style={{ fontSize: 14, fontWeight: 600, overflowWrap: "anywhere" }}>{t.msg}</p>
        {t.detail && (
          <p className={t.mono ? "font-mono" : ""} style={{ fontSize: 12, color: "var(--text-mute)", marginTop: 3, wordBreak: "break-word" }}>
            {t.detail}
          </p>
        )}
        {t.link && (
          <Link to={t.link.zu} onClick={onClose} className="ring-focus" style={{ display: "inline-flex", alignItems: "center", gap: 5, marginTop: 7, fontSize: 12.5, fontWeight: 600 }}>
            <Icon name="arrow-right" size={13} />
            {t.link.text}
          </Link>
        )}
        {t.copy && (
          <button
            type="button"
            onClick={copy}
            className="ring-focus"
            style={{ marginTop: 7, fontSize: 11.5, fontWeight: 600, color: copied ? "var(--good)" : "var(--accent-text)", background: "none", border: "none", cursor: "pointer", display: "flex", alignItems: "center", gap: 5, padding: 0 }}
          >
            <Icon name={copied ? "check" : "copy"} size={13} />
            {copied ? "Kopiert!" : "Meldung kopieren"}
          </button>
        )}
      </div>
      <IconBtn type="button" icon="x" size={14} box={24} onClick={onClose} title="Schließen" />
    </div>
  );
}

export function Toaster() {
  const toasts = useStore((s) => s.toasts);
  const removeToast = useStore((s) => s.removeToast);
  return createPortal(
    <div className="toaster" aria-live="polite" aria-relevant="additions">
      {toasts.map((t) => (
        <ToastCard key={t.id} t={t} onClose={() => removeToast(t.id)} />
      ))}
    </div>,
    portalRoot(),
  );
}
